import React from 'react';
import { datetimeFormat, numberFormat } from '@tokenyico/design-system';
import { DsButton, DsExplorerLink, DsIcon, DsTooltip } from '@tokenyico/design-system/dist/reactJS';
import { dateFormat } from '@tokenyico/design-system';
import { connect } from 'redux-zero/react';
import { useTranslation } from 'react-i18next';
import { displayCurrency, formatExchangeRate } from 'app/helpers/currency';
import { TotalPayment } from '../helpers';
import SubscriptionStatusType from './SubscriptionStatusType';
import { Item, Section } from 'app/views/Platform/views/Orders/helpers';

const SubscriptionsOrderDetailsView = ({ order, user, selectedToken }) => {
  const {
    status,
    tokenPrice,
    investmentCurrency,
    tokenPriceAt,
    docuSignEnvelop,
    createdAt,
    tokensAtCreation,
    exchangeRateAtCreation,
    baseCurrency,
    investmentAmountAtCreation,
    investmentAmountAtPayment,
    feeAtCreation,
    feeAtPayment,
    tokensAtPayment,
    exchangeRateAtPayment,
    paymentNetwork,
    txHash,
    paymentAddress,
    cutOff,
    tradeDate,
  } = order;
  const { t } = useTranslation();
  return (
    <>
      <header>
        <h3>{t('shared.orderDetails')}</h3> <span>{datetimeFormat(createdAt)}</span>
      </header>

      {cutOff && tradeDate ? (
        <div className="calendar _text:xs">
          <div className="row">
            <p className="_text-coolGrey-500">
              {t('shared.cutOffDateTime')}
              <DsTooltip isVisibleOnHover text={t('shared.cutOffTooltip')} className="_mleft-2">
                <DsIcon name="info" size="0.8rem" color="var(--coolGrey-400)" />
              </DsTooltip>
            </p>
            <p className="_text-coolGrey-700">{datetimeFormat(new Date(Date.parse(cutOff)))}</p>
          </div>
          <div className="row">
            <p className="_text-coolGrey-500">
              {t('shared.tradeDate')}
              <DsTooltip isVisibleOnHover text={t('shared.tradeDateTooltip')} className="_mleft-2">
                <DsIcon name="info" size="0.8rem" color="var(--coolGrey-400)" />
              </DsTooltip>
            </p>
            <p className="_text-coolGrey-700">
              {dateFormat(new Date(Date.parse(tradeDate)), true)}
            </p>
          </div>
        </div>
      ) : (
        <></>
      )}

      <div className="order-details">
        <Section>
          <Item
            name={t('shared.status')}
            value={<SubscriptionStatusType status={status} />}
            isMono={false}
          />
          <Item
            name={t('shared.investorWallet')}
            value={
              <DsExplorerLink
                address={user.walletsAndStatus[0].wallet}
                network={selectedToken.blockchainNetwork}
                type="wallet"
                width="160"
              />
            }
            isMono={false}
          />
          {investmentCurrency === 'BTC' && (
            <Item
              name={t('shared.issuerBTCSubaddress')}
              value={
                <DsExplorerLink
                  address={paymentAddress}
                  network={paymentNetwork}
                  type="wallet"
                  width="160"
                />
              }
              isMono={false}
            />
          )}
          <Item
            name={t('shared.tokenPrice')}
            value={tokenPrice ? displayCurrency(tokenPrice, baseCurrency) : '-'}
          />
          <Item
            name={t('shared.tokenPriceTime')}
            value={tokenPriceAt ? datetimeFormat(tokenPriceAt) : <code>{'-'}</code>}
            isMono={false}
          />
          <Item
            name={t('shared.docuSignEnvelop')}
            value={
              docuSignEnvelop ? (
                <DsButton
                  type="button"
                  label={t('shared.view')}
                  mode="outlined"
                  icon="view"
                  iconSize="13px"
                  isReverse
                  isMini
                  onClick={() =>
                    window.open(
                      `https://app.docusign.com/documents/details/${docuSignEnvelop}`,
                      '_blank',
                    )
                  }
                />
              ) : (
                <code>{'-'}</code>
              )
            }
            isMono={false}
          />
          <Item
            name="txHash"
            value={
              txHash ? (
                <DsExplorerLink
                  address={txHash}
                  network={paymentNetwork}
                  type="transaction"
                  width="10rem"
                />
              ) : (
                <code>{'-'}</code>
              )
            }
          />
        </Section>

        <Section>
          <Item
            name={t('shared.tokensAtCreation')}
            value={
              tokensAtCreation
                ? `${numberFormat(tokensAtCreation, selectedToken.decimals)} ${
                    selectedToken.symbol
                  }`
                : '-'
            }
          />
          <Item
            name={t('shared.exchangeRateAtCreation')}
            value={
              exchangeRateAtCreation
                ? formatExchangeRate(exchangeRateAtCreation, baseCurrency, investmentCurrency)
                : '-'
            }
          />
          <Item
            name={t('shared.investmentAtCreation')}
            value={
              investmentAmountAtCreation
                ? displayCurrency(investmentAmountAtCreation, investmentCurrency)
                : '-'
            }
          />
          <Item
            name={t('shared.feeAtCreation')}
            value={feeAtCreation ? displayCurrency(feeAtCreation, investmentCurrency) : '-'}
          />
        </Section>

        <Section>
          <Item
            name={t('shared.amountToPay')}
            value={
              <TotalPayment
                payments={{
                  investment: investmentAmountAtCreation,
                  fee: feeAtCreation,
                }}
                currency={investmentCurrency}
                isMono={true}
              />
            }
          />
        </Section>
        <Section>
          <Item
            name={t('shared.tokensAtPayment')}
            value={
              tokensAtPayment
                ? `${numberFormat(tokensAtPayment, selectedToken.decimals)} ${selectedToken.symbol}`
                : '-'
            }
          />
          <Item
            name={t('shared.exchangeRateAtPayment')}
            value={
              exchangeRateAtPayment
                ? formatExchangeRate(exchangeRateAtPayment, baseCurrency, investmentCurrency)
                : '-'
            }
          />
          <Item
            name={t('shared.investmentAtPayment')}
            value={
              investmentAmountAtPayment
                ? displayCurrency(investmentAmountAtPayment, investmentCurrency)
                : '-'
            }
          />
          <Item
            name={t('shared.feeAtPayment')}
            value={feeAtPayment ? displayCurrency(feeAtPayment, investmentCurrency) : '-'}
          />
        </Section>

        <Section>
          <Item
            name={t('shared.confirmedPayment')}
            value={
              <TotalPayment
                payments={{
                  investment: investmentAmountAtPayment,
                  fee: feeAtPayment,
                }}
                currency={investmentCurrency}
                column="confirmedPayment"
                isMono={true}
              />
            }
          />
        </Section>
      </div>
    </>
  );
};

export default connect((state) => state.holder)(SubscriptionsOrderDetailsView);
